/**
 * Opt-in Firestore SDK debug logging, for diagnosing the cold-load stall
 * tracked in [#3697](https://github.com/CodeinPlace/cip/issues/3697).
 *
 * A HAR shows only what crossed the network. During the stall the client is
 * idle with an open long-poll that delivers nothing, so the interesting state —
 * which targets the SDK considers pending, what it decides about the
 * WebChannel, whether it is waiting or retrying — is never on the wire. This
 * flag turns on the SDK's own log stream so that state is observable.
 *
 * Off unless explicitly requested, because the stream is extremely verbose.
 */

/** Query parameter that turns on Firestore's debug log stream. */
const FIRESTORE_DEBUG_QUERY_PARAM = 'firestoreDebug';

/**
 * Whether this page load asked for Firestore debug logging.
 *
 * Read from the URL rather than an env var so it can be turned on against any
 * already-deployed build — the stall is intermittent and reproduces on hosted
 * dev, so requiring a rebuild would make it much harder to catch.
 *
 * @returns True when the URL carries `?firestoreDebug=1`. False everywhere the
 *   parameter is absent or set to any other value, and in non-browser contexts
 *   (SSR, unit tests) where there is no `window`.
 */
export function isFirestoreDebugLoggingRequested(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const requested = new URLSearchParams(window.location.search).get(
    FIRESTORE_DEBUG_QUERY_PARAM,
  );
  return requested === '1';
}
